import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, Bell, ChevronDown, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/assets";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Check Before You Trust", to: "/check-before-you-trust" },
  { label: "File a Complaint", to: "/file-a-complaint" },
  { label: "Resolve a Dispute", to: "/resolve-a-dispute" },
  { label: "Reviews & Feedback", to: "/reviews" },
  { label: "Vendor Directory", to: "/vendors" },
  { label: "Government Help", to: "/government-help" },
  { label: "Find a Lawyer", to: "/lawyers" },
  { label: "Blog", to: "/blog" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout, openAuth, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
      <div className="ca-container flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="ComplaintAdvisor" className="h-9 w-9 rounded-lg object-contain" />
          <div className="leading-none">
            <span className="font-display font-extrabold text-lg text-slate-900">Complaint<span className="text-teal">Advisor</span></span>
            <p className="hidden lg:block text-[10px] text-slate-400 font-medium tracking-wide">Resolve Before You Escalate</p>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                  isActive ? "bg-teal-brand/10 text-teal" : "text-slate-600 hover:text-teal hover:bg-slate-100"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          <Link to="/file-a-complaint"><Button className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="header-file-complaint">File a Complaint</Button></Link>
          {user && (
            <button onClick={() => navigate("/dashboard")} className="relative p-2 text-slate-600 hover:text-teal" data-testid="notif-bell">
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span className="absolute top-0 right-0 h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-unread-count">{unread}</span>
              )}
            </button>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="account-menu-btn" className="gap-2">
                  <ShieldCheck className="h-4 w-4 text-teal" />
                  {user.name?.split(" ")[0] || "Account"}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> My Dashboard
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" data-testid="login-btn" onClick={() => openAuth("login")}>Login</Button>
              <Button data-testid="signup-btn" className="bg-teal-brand hover:bg-teal-700 text-white" onClick={() => openAuth("signup")}>Sign Up</Button>
            </>
          )}
        </div>

        <button className="xl:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-teal-brand/10 text-teal" : "text-slate-700 hover:bg-slate-50"}`
              }>
              {n.label}
            </NavLink>
          ))}
          <Link to="/file-a-complaint" onClick={() => setOpen(false)}>
            <Button className="w-full bg-teal-brand text-white mb-1" data-testid="mobile-file-complaint">File a Complaint</Button>
          </Link>
          <div className="pt-2 flex gap-2">
            {user ? (
              <>
                <Button className="flex-1" variant="outline" onClick={() => { navigate("/dashboard"); setOpen(false); }}>Dashboard</Button>
                <Button className="flex-1" onClick={() => { logout(); setOpen(false); }}>Logout</Button>
              </>
            ) : (
              <>
                <Button className="flex-1" variant="outline" onClick={() => { openAuth("login"); setOpen(false); }}>Login</Button>
                <Button className="flex-1 bg-teal-brand text-white" onClick={() => { openAuth("signup"); setOpen(false); }}>Sign Up</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
